import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import TopBarProgress from "react-topbar-progress-indicator";
import { BusinessReportPrompt, BusinessReportType } from "@/modules/snapshots/businessReports.types";
import { useUpdateBusinessReportPromptMutation } from "@/modules/snapshots/businessReports.api";
import { BusinessReportPromptDisplay } from "@/modules/snapshots/components/BusinessReportPromptForm";
import { BusinessReportPromptEditModal } from "@/modules/snapshots/components/BusinessReportPromptEditModal";
import type { PromptEditFormInputs } from "@/modules/snapshots/components/BusinessReportPromptEditModal.utils";

interface Props {
  prompt: BusinessReportPrompt;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  templateType?: BusinessReportType;
  /** Library admins and the owner of an authoring template; everyone else only reads. */
  canEdit?: boolean;
}

export const PromptItem: React.FC<Props> = ({ prompt, isExpanded, onToggle, templateType, canEdit = true }) => {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [handleUpdate, { isLoading }] = useUpdateBusinessReportPromptMutation();

  const onSave = async (payload: PromptEditFormInputs) => {
    await handleUpdate({ id: prompt.id, data: payload }).unwrap();
    toast.success("Change saved successfully!");
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  return (
    <>
      <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 shadow-sm transition-all hover:border-desert-sand-300">
        {isLoading && <TopBarProgress />}
        <button
          type="button"
          onClick={() => onToggle(prompt.id)}
          className="from-desert-sand-50/80 to-desert-sand-100/50 hover:from-desert-sand-100/80 hover:to-desert-sand-150/50 flex w-full cursor-pointer items-center justify-between bg-gradient-to-r px-7 py-5 transition-colors"
        >
          <div className="flex items-center gap-5">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-2 rounded-full bg-desert-sand-400" />
              <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-midnight-blue-600 text-sm font-medium text-white">
                {prompt.order + 1}
              </span>
            </div>
            <div className="bg-ui-card-background/40 flex flex-col gap-0.5 rounded-md px-3 py-1.5 backdrop-blur-sm">
              <h4 className="font-medium text-gray-900">{prompt.name}</h4>
            </div>
          </div>
          <div className="text-midnight-blue-500 transition-transform duration-200 ease-in-out">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>
        {isExpanded && (
          <BusinessReportPromptDisplay
            prompt={prompt}
            onEdit={canEdit ? handleEdit : undefined}
            templateType={templateType}
          />
        )}
      </div>

      <BusinessReportPromptEditModal
        prompt={prompt}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={onSave}
        isLoading={isLoading}
        templateType={templateType}
        title={templateType === "authoring_template" ? "Edit check" : undefined}
      />
    </>
  );
};
